// src/components/RootCause/RootCauseView.jsx
// Root-Cause Investigation — AI-powered pipeline failure analysis
import React, { useState, useEffect, useRef } from 'react';
import {
  Search, RefreshCw, ChevronRight, ChevronDown,
  AlertTriangle, CheckCircle, Clock, Zap, Brain,
  GitBranch, Layers, Activity, Database, Globe, Server,
  Shield, Cpu, ArrowRight, Flame, X, Copy, Check,
  BarChart2, ListChecks, Microscope, Network, FileText,
  TrendingDown, TrendingUp, Eye, Play, Pause,
} from 'lucide-react';

// ─── Color palette ────────────────────────────────────────────────────────────
const SEV = {
  CRITICAL: { color: '#ef4444', bg: 'rgba(239,68,68,.12)',  badge: 'rgba(239,68,68,.18)',  label: 'Critical' },
  HIGH:     { color: '#f97316', bg: 'rgba(249,115,22,.12)', badge: 'rgba(249,115,22,.18)', label: 'High'     },
  MEDIUM:   { color: '#f59e0b', bg: 'rgba(245,158,11,.12)', badge: 'rgba(245,158,11,.18)', label: 'Medium'   },
};
const STATUS = {
  INVESTIGATING: { color: '#f97316', label: 'Investigating', dot: true  },
  CONFIRMED:     { color: '#ef4444', label: 'RCA Confirmed', dot: true  },
  MITIGATED:     { color: '#f59e0b', label: 'Mitigated',     dot: true  },
  RESOLVED:      { color: '#22c55e', label: 'Resolved',      dot: false },
};
const EVIDENCE_TYPE = {
  log:     { color: '#60a5fa', label: 'Log'     },
  metric:  { color: '#34d399', label: 'Metric'  },
  trace:   { color: '#c084fc', label: 'Trace'   },
  alert:   { color: '#f87171', label: 'Alert'   },
  change:  { color: '#fbbf24', label: 'Change'  },
};
const T_NOW = Date.now();
function minsAgo(m) { return new Date(T_NOW - m * 60000).toISOString(); }

// ─── Demo incident data ───────────────────────────────────────────────────────
const INCIDENTS = [
  {
    id: 'INC-2041',
    title: 'Database Connection Pool Exhaustion — Cascading Payment Failure',
    pipeline: 'payment-processing-pipeline',
    severity: 'CRITICAL',
    status: 'RESOLVED',
    detectedAt: minsAgo(187),
    resolvedAt: minsAgo(62),
    mttd: 8,
    mttr: 125,
    services: ['payment-api', 'order-service', 'inventory-service', 'postgres-primary'],
    rcaConfidence: 97,
    summary: 'A surge in payment retries caused PostgreSQL connection pool exhaustion (pool_size=50). Connection queue backed up across 3 downstream services, triggering 847 failed transactions over 23 minutes before auto-scaling resolved it.',
    timeline: [
      { t: minsAgo(187), type: 'alert',   sev: 'MEDIUM', src: 'PostgreSQL', msg: 'Connection wait time exceeded 2s threshold (avg: 2.4s)' },
      { t: minsAgo(184), type: 'metric',  sev: 'HIGH',   src: 'payment-api',msg: 'Outbound DB connections hit 48/50 (96% pool utilization)' },
      { t: minsAgo(181), type: 'log',     sev: 'HIGH',   src: 'order-service', msg: 'ERROR: could not connect to server: connection refused — pool exhausted' },
      { t: minsAgo(179), type: 'alert',   sev: 'CRITICAL', src: 'payment-api', msg: 'Payment success rate dropped: 100% → 34% in 90 seconds' },
      { t: minsAgo(175), type: 'log',     sev: 'CRITICAL', src: 'inventory-service', msg: 'CRITICAL: upstream dependency unavailable — order-service returning 503' },
      { t: minsAgo(172), type: 'change',  sev: 'MEDIUM', src: 'deployment', msg: 'payment-retry-lambda deployed (v2.4.1) — retry backoff reduced 30s→5s' },
      { t: minsAgo(168), type: 'trace',   sev: 'HIGH',   src: 'jaeger',     msg: 'P99 span latency: payment/checkout → postgres: 18,400ms (normal: 120ms)' },
      { t: minsAgo(165), type: 'alert',   sev: 'CRITICAL', src: 'SLO-monitor', msg: 'SLO breach: payment availability 34.2% (target: 99.9%)' },
      { t: minsAgo(150), type: 'change',  sev: 'LOW',    src: 'ops',        msg: 'Manual: pool_size increased 50 → 150, pgbouncer restarted' },
      { t: minsAgo(138), type: 'metric',  sev: 'LOW',    src: 'payment-api',msg: 'Connection pool utilization recovering: 62% → 28%' },
      { t: minsAgo(62),  type: 'alert',   sev: 'LOW',    src: 'SLO-monitor',msg: 'SLO restored: payment availability 99.92%' },
    ],
    hypotheses: [
      {
        id: 'h1', rank: 1, confidence: 97,
        title: 'Retry storm triggered by v2.4.1 deployment reduced backoff',
        category: 'Deployment Change',
        color: '#ef4444',
        evidence: [
          { type: 'change', text: 'payment-retry-lambda v2.4.1 deployed 15min before incident — retry backoff reduced 30s→5s' },
          { type: 'metric', text: 'Retry rate spiked 12x from baseline within 2 min of deployment (8 req/s → 94 req/s)' },
          { type: 'log',    text: '14,722 "payment_retry" log lines in 20-min window vs 1,200 typical baseline' },
          { type: 'trace',  text: 'All failed payments show identical retry pattern originating from lambda v2.4.1' },
        ],
        remediation: 'Roll back payment-retry-lambda to v2.4.0 or increase retry backoff ≥ 30s. Enforce circuit breaker on DB pool > 80% utilization.',
      },
      {
        id: 'h2', rank: 2, confidence: 18,
        title: 'PostgreSQL max_connections misconfigured after last maintenance',
        category: 'Configuration Drift',
        color: '#f97316',
        evidence: [
          { type: 'metric', text: 'DB max_connections = 100 (pool_size=50 per app instance × 2 instances = 100 exactly)' },
          { type: 'log',    text: 'postgres logs show "sorry, too many clients" at same timestamp as incident start' },
        ],
        remediation: 'Increase max_connections to 500 and configure pgbouncer transaction pooling mode.',
      },
    ],
    blastRadius: [
      { service: 'payment-api',       impact: 'CRITICAL', type: 'API',      affected: '847 txn failed',    color: '#ef4444' },
      { service: 'order-service',      impact: 'HIGH',     type: 'API',      affected: '503 errors 23min',  color: '#f97316' },
      { service: 'inventory-service',  impact: 'HIGH',     type: 'API',      affected: 'reads degraded',    color: '#f97316' },
      { service: 'postgres-primary',   impact: 'CRITICAL', type: 'Database', affected: 'pool exhausted',    color: '#ef4444' },
      { service: 'notification-svc',   impact: 'MEDIUM',   type: 'Worker',   affected: 'queue backed up',   color: '#f59e0b' },
      { service: 'analytics-pipeline', impact: 'LOW',      type: 'Pipeline', affected: 'event loss ~2%',    color: '#94a3b8' },
    ],
    playbook: [
      { step: 1, action: 'Identify connection pool utilization source via pg_stat_activity', status: 'done',    owner: 'auto' },
      { step: 2, action: 'Correlate with recent deployments in the 30-min window',           status: 'done',    owner: 'auto' },
      { step: 3, action: 'Increase pgbouncer pool_size: 50 → 150 and restart',               status: 'done',    owner: 'ops'  },
      { step: 4, action: 'Roll back payment-retry-lambda to v2.4.0',                         status: 'done',    owner: 'eng'  },
      { step: 5, action: 'Verify SLO recovery — payment success rate > 99%',                 status: 'done',    owner: 'auto' },
      { step: 6, action: 'Add circuit breaker: DB pool > 80% → reject retries for 60s',     status: 'pending', owner: 'eng'  },
      { step: 7, action: 'Update runbook with retry backoff requirements',                    status: 'pending', owner: 'docs' },
    ],
    correlations: {
      logs: [
        { time: minsAgo(181), src: 'order-service', level: 'ERROR', msg: 'PG: could not connect — pool exhausted (pool_size=50, waiting=50)' },
        { time: minsAgo(179), src: 'payment-api',   level: 'ERROR', msg: 'DB acquire timeout after 5000ms — circuit not open' },
        { time: minsAgo(175), src: 'payment-api',   level: 'ERROR', msg: 'payment_retry_attempt=4 transactionId=txn-38291 — final attempt' },
      ],
      metrics: [
        { name: 'pg.pool.utilization',     value: '96%',     delta: '+84%', trend: 'up',   color: '#ef4444' },
        { name: 'payment.success_rate',    value: '34.2%',   delta: '-65%', trend: 'down', color: '#ef4444' },
        { name: 'retry.rate_per_sec',      value: '94 req/s',delta: '+11x', trend: 'up',   color: '#f97316' },
        { name: 'p99.db_latency',          value: '18,400ms',delta: '+152x',trend: 'up',   color: '#ef4444' },
      ],
    },
  },
  {
    id: 'INC-2038',
    title: 'Memory Leak in API Gateway — Gradual OOM + Service Restart Loop',
    pipeline: 'api-gateway-cluster',
    severity: 'HIGH',
    status: 'CONFIRMED',
    detectedAt: minsAgo(310),
    resolvedAt: null,
    mttd: 22,
    mttr: null,
    services: ['api-gateway-v2', 'auth-service', 'rate-limiter', 'redis-cluster'],
    rcaConfidence: 91,
    summary: 'Go garbage collector in api-gateway-v2 fails to reclaim memory from WebSocket connection objects after disconnect. Heap grows ~40MB/hour, causing OOM kill every ~6h with a 45-second service gap.',
    timeline: [
      { t: minsAgo(310), type: 'metric', sev: 'LOW',      src: 'k8s',         msg: 'api-gateway-v2 heap usage trending up: 380MB (baseline: 220MB)' },
      { t: minsAgo(289), type: 'metric', sev: 'MEDIUM',   src: 'k8s',         msg: 'heap: 520MB, GC pressure increasing (GC cycles +340%)' },
      { t: minsAgo(268), type: 'alert',  sev: 'MEDIUM',   src: 'prometheus',  msg: 'Memory usage alert: api-gateway-v2 > 80% of 640Mi limit' },
      { t: minsAgo(244), type: 'log',    sev: 'HIGH',     src: 'api-gateway', msg: 'runtime: out of memory: cannot allocate 24576-byte block' },
      { t: minsAgo(244), type: 'alert',  sev: 'CRITICAL', src: 'k8s',         msg: 'OOMKilled: api-gateway-v2 pod restarted (restart #1)' },
      { t: minsAgo(242), type: 'metric', sev: 'HIGH',     src: 'downstream',  msg: '45-second gap: 1,840 requests returned 503 during restart' },
      { t: minsAgo(230), type: 'trace',  sev: 'MEDIUM',   src: 'jaeger',      msg: 'goroutine leak detected: 12,400 goroutines (normal: 340)' },
      { t: minsAgo(182), type: 'alert',  sev: 'CRITICAL', src: 'k8s',         msg: 'OOMKilled: api-gateway-v2 pod restarted (restart #2)' },
    ],
    hypotheses: [
      {
        id: 'h1', rank: 1, confidence: 91,
        title: 'WebSocket connection goroutine leak — handler not calling conn.Close() on client disconnect',
        category: 'Memory Leak',
        color: '#f97316',
        evidence: [
          { type: 'trace',  text: '12,400 goroutines stuck in wsHandler.readPump() — should be ≤ active connections (340)' },
          { type: 'metric', text: 'heap growth rate: +40MB/hour matches WS disconnect rate × connection object size' },
          { type: 'log',    text: 'No "ws_connection_closed" events in logs — close handler never fires' },
          { type: 'change', text: 'api-gateway v2.8.0 introduced WebSocket upgrade support 3 days ago' },
        ],
        remediation: 'Add defer conn.Close() in wsHandler. Use context cancellation pattern. Memory limit raise to 1Gi as temporary mitigation.',
      },
    ],
    blastRadius: [
      { service: 'api-gateway-v2',  impact: 'CRITICAL', type: 'Gateway',  affected: '45s downtime per OOM',   color: '#ef4444' },
      { service: 'auth-service',    impact: 'HIGH',     type: 'Auth',     affected: 'JWT validation gap',      color: '#f97316' },
      { service: 'rate-limiter',    impact: 'MEDIUM',   type: 'Proxy',    affected: 'bypass during restart',   color: '#f59e0b' },
      { service: 'mobile-clients',  impact: 'HIGH',     type: 'Client',   affected: '~1,840 req/restart fail', color: '#f97316' },
      { service: 'redis-cluster',   impact: 'LOW',      type: 'Cache',    affected: 'session invalidation',    color: '#94a3b8' },
    ],
    playbook: [
      { step: 1, action: 'Confirm OOMKill pattern via k8s events and pod restart count',             status: 'done',        owner: 'auto' },
      { step: 2, action: 'Capture goroutine dump via pprof /debug/pprof/goroutine',                  status: 'done',        owner: 'eng'  },
      { step: 3, action: 'Increase memory limit: 640Mi → 1Gi (buys ~6h before next OOM)',            status: 'done',        owner: 'ops'  },
      { step: 4, action: 'Fix wsHandler: add defer conn.Close() + context cancellation',              status: 'in-progress', owner: 'eng'  },
      { step: 5, action: 'Deploy patched api-gateway v2.8.1 to staging, run 2h soak test',           status: 'pending',     owner: 'eng'  },
      { step: 6, action: 'Deploy to production, monitor heap for 30 minutes',                        status: 'pending',     owner: 'ops'  },
    ],
    correlations: {
      logs: [
        { time: minsAgo(244), src: 'api-gateway', level: 'FATAL', msg: 'runtime: out of memory: cannot allocate 24576-byte block (heap=638MB)' },
        { time: minsAgo(230), src: 'api-gateway', level: 'WARN',  msg: 'goroutine count anomaly: 12,400 goroutines — leak suspected' },
        { time: minsAgo(182), src: 'k8s-kubelet', level: 'ERROR', msg: 'OOMKilled container api-gateway-v2 in pod api-gw-prod-7d9f8 (exit 137)' },
      ],
      metrics: [
        { name: 'process.heap_alloc',       value: '638 MB', delta: '+418MB', trend: 'up',   color: '#ef4444' },
        { name: 'runtime.goroutines',        value: '12,400', delta: '+36x',   trend: 'up',   color: '#ef4444' },
        { name: 'gc.pause_total_ms',         value: '2,840ms',delta: '+580%',  trend: 'up',   color: '#f97316' },
        { name: 'http.5xx_rate',             value: '8.4/s',  delta: '+8.4',   trend: 'up',   color: '#f97316' },
      ],
    },
  },
  {
    id: 'INC-2035',
    title: 'Kafka Consumer Lag Spike — Batch Analytics Pipeline Stalled',
    pipeline: 'analytics-batch-pipeline',
    severity: 'CRITICAL',
    status: 'MITIGATED',
    detectedAt: minsAgo(520),
    resolvedAt: null,
    mttd: 4,
    mttr: null,
    services: ['kafka-cluster', 'analytics-consumer', 'clickhouse-writer', 'data-lake-exporter'],
    rcaConfidence: 89,
    summary: 'ClickHouse write latency increase caused consumer-side backpressure. Consumer lag grew from 0 to 4.8M messages over 2 hours. Root cause: ClickHouse disk I/O saturation from unthrottled merge operations during peak ingestion.',
    timeline: [
      { t: minsAgo(520), type: 'metric', sev: 'LOW',      src: 'kafka',       msg: 'Consumer lag: analytics-consumer-group offset growing (lag: 12k → 48k)' },
      { t: minsAgo(510), type: 'metric', sev: 'MEDIUM',   src: 'clickhouse',  msg: 'Insert latency p99 increasing: 240ms → 1,800ms' },
      { t: minsAgo(498), type: 'alert',  sev: 'HIGH',     src: 'prometheus',  msg: 'Kafka consumer lag > 500k messages on analytics-consumer-group' },
      { t: minsAgo(490), type: 'metric', sev: 'HIGH',     src: 'clickhouse',  msg: 'Disk I/O util: 94% (sda writes: 1.4 GB/s sustained — saturation)' },
      { t: minsAgo(482), type: 'log',    sev: 'HIGH',     src: 'analytics-consumer', msg: 'WARN: batch_write_timeout after 30s — 42,000 events dropped to DLQ' },
      { t: minsAgo(470), type: 'alert',  sev: 'CRITICAL', src: 'prometheus',  msg: 'Consumer lag > 2M messages — analytics delay: 47 minutes' },
      { t: minsAgo(455), type: 'change', sev: 'MEDIUM',   src: 'clickhouse',  msg: 'Manual: background_pool_size increased, merge throttle enabled' },
      { t: minsAgo(440), type: 'metric', sev: 'LOW',      src: 'clickhouse',  msg: 'Disk I/O normalizing: 94% → 62%. Insert latency recovering.' },
    ],
    hypotheses: [
      {
        id: 'h1', rank: 1, confidence: 89,
        title: 'ClickHouse unthrottled merge caused I/O saturation → consumer backpressure',
        category: 'Resource Saturation',
        color: '#ef4444',
        evidence: [
          { type: 'metric', text: 'CH disk I/O saturation (94%) perfectly correlates with consumer lag spike onset (-2min)' },
          { type: 'metric', text: 'Active parts count: 8,400 (threshold: 300) — excessive merge queue building up' },
          { type: 'log',    text: 'ClickHouse system.parts shows 8,400 active parts — merge falling behind inserts' },
          { type: 'change', text: 'Analytics ingestion rate increased 3.2x at 06:00 UTC (daily batch job started)' },
        ],
        remediation: 'Set background_pool_size=4, merge_tree.parts_to_throw_insert=600. Implement insert throttle when active_parts > 1000.',
      },
    ],
    blastRadius: [
      { service: 'analytics-consumer',   impact: 'CRITICAL', type: 'Worker',   affected: '4.8M msg backlog',    color: '#ef4444' },
      { service: 'clickhouse-writer',     impact: 'CRITICAL', type: 'Database', affected: 'I/O saturated 94%',   color: '#ef4444' },
      { service: 'data-lake-exporter',    impact: 'HIGH',     type: 'Worker',   affected: '2h data delay',       color: '#f97316' },
      { service: 'reporting-service',     impact: 'HIGH',     type: 'API',      affected: 'stale dashboards',    color: '#f97316' },
      { service: 'ml-feature-store',      impact: 'MEDIUM',   type: 'Pipeline', affected: 'feature freshness +3h', color: '#f59e0b' },
    ],
    playbook: [
      { step: 1, action: 'Verify consumer lag growth rate and DLQ message count',                    status: 'done',        owner: 'auto' },
      { step: 2, action: 'Identify bottleneck: ClickHouse I/O saturation confirmed via iostat',      status: 'done',        owner: 'eng'  },
      { step: 3, action: 'Enable merge throttle: SET background_pool_size=4 on CH cluster',          status: 'done',        owner: 'ops'  },
      { step: 4, action: 'Reduce analytics-consumer batch_size 10k → 2k to reduce write pressure',  status: 'done',        owner: 'eng'  },
      { step: 5, action: 'Replay DLQ messages (42k events) via backfill job',                        status: 'in-progress', owner: 'eng'  },
      { step: 6, action: 'Add alert: CH active_parts > 500 + auto-throttle consumer group',         status: 'pending',     owner: 'eng'  },
    ],
    correlations: {
      logs: [
        { time: minsAgo(482), src: 'analytics-consumer', level: 'WARN',  msg: 'batch_write_timeout=30s exceeded — 42,000 events written to DLQ' },
        { time: minsAgo(470), src: 'clickhouse',         level: 'ERROR', msg: "Too many parts (8400 > 300) in events_v2. Won't insert for 1s." },
        { time: minsAgo(455), src: 'clickhouse',         level: 'INFO',  msg: 'Throttling inserts: active_parts=8400 background_pool exhausted' },
      ],
      metrics: [
        { name: 'kafka.consumer.lag',         value: '4.8M msg', delta: '+4.8M',   trend: 'up',   color: '#ef4444' },
        { name: 'clickhouse.disk_io_util',    value: '94%',      delta: '+71%',    trend: 'up',   color: '#ef4444' },
        { name: 'clickhouse.active_parts',    value: '8,400',    delta: '+27x',    trend: 'up',   color: '#f97316' },
        { name: 'analytics.event_delay_min',  value: '47 min',   delta: '+47min',  trend: 'up',   color: '#f97316' },
      ],
    },
  },
  {
    id: 'INC-2031',
    title: 'SSL Certificate Expiry — Auth Failures Across 8 Microservices',
    pipeline: 'auth-infrastructure',
    severity: 'CRITICAL',
    status: 'RESOLVED',
    detectedAt: minsAgo(1440),
    resolvedAt: minsAgo(1380),
    mttd: 3,
    mttr: 60,
    services: ['cert-manager', 'auth-service', 'api-gateway', 'inter-service-mesh'],
    rcaConfidence: 100,
    summary: 'Production TLS certificate for *.internal.prod expired at 03:14 UTC. Certificate rotation automation failed silently due to cert-manager RBAC permissions revoked during last cluster upgrade. 8 services rejected mutual TLS connections for 60 minutes.',
    timeline: [
      { t: minsAgo(1443), type: 'alert',  sev: 'HIGH',     src: 'cert-monitor', msg: 'TLS cert *.internal.prod expires in 0h 3m (should have rotated 30d ago)' },
      { t: minsAgo(1440), type: 'alert',  sev: 'CRITICAL', src: 'auth-service', msg: 'x509: certificate has expired — 100% of mTLS handshakes failing' },
      { t: minsAgo(1438), type: 'alert',  sev: 'CRITICAL', src: 'api-gateway',  msg: 'Upstream TLS error on all internal routes — 100% 503 rate' },
      { t: minsAgo(1435), type: 'log',    sev: 'CRITICAL', src: 'cert-manager', msg: 'ERROR: CertificateRequest denied — rolebinding "cert-manager-issuer" not found' },
      { t: minsAgo(1420), type: 'change', sev: 'HIGH',     src: 'ops',          msg: 'Manual RBAC restore: CertificateRequest ClusterRole re-applied' },
      { t: minsAgo(1415), type: 'log',    sev: 'LOW',      src: 'cert-manager', msg: 'INFO: Certificate issued successfully — *.internal.prod (valid 90d)' },
      { t: minsAgo(1382), type: 'metric', sev: 'LOW',      src: 'auth-service', msg: 'mTLS success rate recovering: 0% → 99.8% (cert propagation ~18min)' },
    ],
    hypotheses: [
      {
        id: 'h1', rank: 1, confidence: 100,
        title: 'cert-manager RBAC ClusterRole binding removed during k8s 1.28 upgrade',
        category: 'Infrastructure Change',
        color: '#ef4444',
        evidence: [
          { type: 'log',    text: 'cert-manager logs: "CertificateRequest denied — rolebinding not found" for 30 days' },
          { type: 'change', text: 'k8s 1.27 → 1.28 upgrade 31 days ago removed cert-manager ClusterRoleBinding (known regression)' },
          { type: 'alert',  text: 'No alert fired when auto-rotation failed 30d ago — missing "cert rotation failure" monitor' },
          { type: 'metric', text: 'Cert expiry alert only fires 3min before expiry (should be 30d)' },
        ],
        remediation: 'Restore cert-manager ClusterRoleBinding. Set cert expiry alert to 30d. Add CertificateRequest failure alert. Automate RBAC validation post-upgrade.',
      },
    ],
    blastRadius: [
      { service: 'auth-service',     impact: 'CRITICAL', type: 'Auth',     affected: '100% mTLS fail 60min', color: '#ef4444' },
      { service: 'api-gateway',      impact: 'CRITICAL', type: 'Gateway',  affected: '100% 503 60min',       color: '#ef4444' },
      { service: 'payment-api',      impact: 'CRITICAL', type: 'API',      affected: 'auth blocked 60min',   color: '#ef4444' },
      { service: 'order-service',    impact: 'CRITICAL', type: 'API',      affected: 'auth blocked 60min',   color: '#ef4444' },
      { service: 'user-service',     impact: 'HIGH',     type: 'API',      affected: 'login failures',       color: '#f97316' },
      { service: 'notification-svc', impact: 'MEDIUM',   type: 'Worker',   affected: 'webhook failures',     color: '#f59e0b' },
    ],
    playbook: [
      { step: 1, action: 'Confirm cert expiry via: openssl s_client -connect auth-service:443',      status: 'done', owner: 'auto' },
      { step: 2, action: 'Identify cert-manager RBAC failure in logs',                               status: 'done', owner: 'auto' },
      { step: 3, action: 'kubectl apply ClusterRoleBinding cert-manager-issuer',                     status: 'done', owner: 'ops'  },
      { step: 4, action: 'Trigger manual cert renewal: kubectl cert-manager renew --all',            status: 'done', owner: 'ops'  },
      { step: 5, action: 'Monitor cert propagation across all pods (~15-20 min)',                    status: 'done', owner: 'auto' },
      { step: 6, action: 'Add 30-day cert expiry alert + cert-rotation failure alert',               status: 'done', owner: 'eng'  },
      { step: 7, action: 'Add RBAC validation step to cluster upgrade runbook',                      status: 'done', owner: 'docs' },
    ],
    correlations: {
      logs: [
        { time: minsAgo(1440), src: 'auth-service', level: 'ERROR', msg: 'tls: failed to verify certificate: x509: certificate has expired (NotAfter 2026-06-08 03:14:00 +0000)' },
        { time: minsAgo(1435), src: 'cert-manager', level: 'ERROR', msg: 'CertificateRequest "internal-prod-tls" denied: no ClusterRoleBinding for "cert-manager-issuer"' },
        { time: minsAgo(1415), src: 'cert-manager', level: 'INFO',  msg: 'Certificate "internal-prod-tls" issued, valid until 2026-09-07' },
      ],
      metrics: [
        { name: 'tls.handshake_failures',  value: '100%',  delta: '+100%', trend: 'up',   color: '#ef4444' },
        { name: 'cert.days_until_expiry',  value: '0',     delta: '-365',  trend: 'down', color: '#ef4444' },
        { name: 'http.503_rate',           value: '100%',  delta: '+100%', trend: 'up',   color: '#ef4444' },
        { name: 'auth.success_rate',       value: '0%',    delta: '-100%', trend: 'down', color: '#ef4444' },
      ],
    },
  },
];

// ─── Aggregate stats ──────────────────────────────────────────────────────────
const resolved  = INCIDENTS.filter(i => i.status === 'RESOLVED');
const avgMttd   = Math.round(INCIDENTS.reduce((a, b) => a + b.mttd, 0) / INCIDENTS.length);
const avgMttr   = Math.round(resolved.reduce((a, b) => a + (b.mttr || 0), 0) / resolved.length);

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmt(ts) {
  const d = new Date(ts);
  return d.toLocaleString('en-US', { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit', hour12: false });
}
function diffMins(a, b) { return Math.round((new Date(a) - new Date(b)) / 60000); }
function sinceMin(ts)    { return Math.abs(diffMins(new Date(), new Date(ts))); }

// ─── Status badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const cfg = STATUS[status] || { color: '#94a3b8', label: status };
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
      background: `${cfg.color}18`, color: cfg.color, border: `1px solid ${cfg.color}30`,
      display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      {cfg.dot && <span style={{ width: 5, height: 5, borderRadius: '50%', background: cfg.color, display: 'inline-block' }}/>}
      {cfg.label}
    </span>
  );
}

// ─── Evidence chip ────────────────────────────────────────────────────────────
function EvidenceChip({ type }) {
  const cfg = EVIDENCE_TYPE[type] || { color: '#94a3b8', label: type };
  return (
    <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
      background: `${cfg.color}18`, color: cfg.color, border: `1px solid ${cfg.color}25` }}>
      {cfg.label}
    </span>
  );
}

// ─── Timeline row ─────────────────────────────────────────────────────────────
function TimelineRow({ e, first, last }) {
  const [expanded, setExpanded] = useState(false);
  const cfg  = SEV[e.sev] || SEV.MEDIUM;
  const tCfg = EVIDENCE_TYPE[e.type] || { color: '#94a3b8' };
  return (
    <div className="flex gap-3">
      {/* Spine */}
      <div className="flex flex-col items-center" style={{ width: 24, flexShrink: 0 }}>
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: tCfg.color, border: `2px solid ${tCfg.color}`, flexShrink: 0, marginTop: 2 }}/>
        {!last && <div style={{ flex: 1, width: 2, background: 'var(--border)', minHeight: 20 }}/>}
      </div>
      {/* Content */}
      <div className="flex-1 pb-3">
        <div className="flex items-center gap-2 flex-wrap mb-0.5 cursor-pointer" onClick={() => setExpanded(x => !x)}>
          <span className="mono" style={{ fontSize: 10, color: 'var(--text-3)' }}>{fmt(e.t)}</span>
          <EvidenceChip type={e.type} />
          <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 4, background: cfg.bg, color: cfg.color }}>{e.sev}</span>
          <span className="text-xs font-semibold" style={{ color: 'var(--text-3)' }}>{e.src}</span>
          <span style={{ marginLeft:'auto', fontSize:9, color:'var(--text-3)' }}>{expanded ? '▲' : '▼'}</span>
        </div>
        <p className="text-xs leading-relaxed" style={{ color: 'var(--text-2)' }}>{e.msg}</p>
        {expanded && (
          <div className="mt-2 rounded-lg p-3" style={{ background:'var(--bg-tertiary)', border:`1px solid ${tCfg.color}20` }}>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
              {[
                ['Source',    e.src],
                ['Type',      EVIDENCE_TYPE[e.type]?.label || e.type],
                ['Severity',  e.sev],
                ['Timestamp', fmt(e.t)],
              ].map(([k,v]) => (
                <div key={k} className="flex items-center gap-1">
                  <span style={{ color:'var(--text-3)', minWidth:60 }}>{k}:</span>
                  <span className="mono font-semibold" style={{ color:k==='Source'?tCfg.color:k==='Severity'?cfg.color:'var(--text-2)' }}>{v}</span>
                </div>
              ))}
            </div>
            <div className="mt-2 pt-2 text-xs leading-relaxed" style={{ borderTop:`1px solid ${tCfg.color}15`, color:'var(--text-2)' }}>{e.msg}</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Confidence ring ──────────────────────────────────────────────────────────
function ConfidenceRing({ pct, color, size = 44 }) {
  const r  = (size - 6) / 2;
  const c  = size / 2;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  return (
    <svg width={size} height={size} style={{ flexShrink: 0 }}>
      <circle cx={c} cy={c} r={r} fill="none" stroke="var(--border)" strokeWidth={4}/>
      <circle cx={c} cy={c} r={r} fill="none" stroke={color} strokeWidth={4}
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
        transform={`rotate(-90 ${c} ${c})`}/>
      <text x={c} y={c+1} textAnchor="middle" dominantBaseline="middle"
        fontSize={10} fontWeight={700} fill={color} fontFamily="Space Mono,monospace">{pct}%</text>
    </svg>
  );
}

// ─── Blast Radius Tab ─────────────────────────────────────────────────────────
function BlastRadiusTab({ inc }) {
  const [selService, setSelService] = useState(null);
  return (
    <div className="flex flex-col gap-4">
      <div className="text-xs mb-1" style={{ color: 'var(--text-3)' }}>
        Click a service to inspect its impact, logs, and timeline events
      </div>
      <div className="flex flex-col gap-2">
        {inc.blastRadius.map(s => {
          const impCfg = { CRITICAL: '#ef4444', HIGH: '#f97316', MEDIUM: '#f59e0b', LOW: '#94a3b8' };
          const ic = impCfg[s.impact] || '#94a3b8';
          return (
            <div key={s.service} className="flex items-center gap-3 px-4 py-3 rounded-xl cursor-pointer hover:scale-[1.01] transition-transform"
              style={{ background: `${ic}07`, border: `1px solid ${ic}20` }}
              onClick={() => setSelService(s)}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: ic, flexShrink: 0 }}/>
              <span className="font-mono text-xs font-semibold" style={{ color: ic, width: 180, flexShrink: 0 }}>{s.service}</span>
              <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: `${ic}18`, color: ic, flexShrink: 0 }}>{s.impact}</span>
              <span className="text-xs" style={{ color: 'var(--text-3)', flexShrink: 0, width: 70 }}>{s.type}</span>
              <span className="text-xs flex-1" style={{ color: 'var(--text-2)' }}>{s.affected}</span>
              <ChevronRight size={12} style={{ color:'var(--text-3)', flexShrink:0 }}/>
            </div>
          );
        })}
      </div>
      {selService && <ServiceDetailPanel service={selService} inc={inc} onClose={() => setSelService(null)}/>}
    </div>
  );
}

// ─── Tabs ─────────────────────────────────────────────────────────────────────
const TABS = ['Overview','Timeline','AI Analysis','Blast Radius','Correlations','Playbook'];

// ─── Investigation panel ──────────────────────────────────────────────────────
function InvestigationPanel({ inc }) {
  const [tab, setTab] = useState('Overview');
  const sev  = SEV[inc.severity] || SEV.MEDIUM;
  const dur  = inc.resolvedAt
    ? diffMins(inc.resolvedAt, inc.detectedAt)
    : sinceMin(inc.detectedAt);

  return (
    <div className="flex flex-col h-full">
      {/* Panel header */}
      <div className="px-5 py-4 shrink-0" style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)' }}>
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: sev.bg, border: `1px solid ${sev.color}30` }}>
            <Flame size={18} style={{ color: sev.color }}/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="mono text-xs font-bold" style={{ color: sev.color }}>{inc.id}</span>
              <StatusBadge status={inc.status}/>
              <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                background: sev.bg, color: sev.color }}>{inc.severity}</span>
            </div>
            <h2 className="font-bold text-sm leading-snug">{inc.title}</h2>
            <div className="flex items-center gap-3 mt-1 text-xs flex-wrap" style={{ color: 'var(--text-3)' }}>
              <span>🔧 {inc.pipeline}</span>
              <span>🕐 Detected {sinceMin(inc.detectedAt)}m ago</span>
              <span>⏱ Duration: {dur}m</span>
              <span>🎯 {inc.services.length} services affected</span>
            </div>
          </div>
          <ConfidenceRing pct={inc.rcaConfidence} color={sev.color} size={52}/>
        </div>
        {/* Tabs */}
        <div className="flex gap-0" style={{ marginBottom: -1 }}>
          {TABS.map(t => (
            <button key={t} onClick={() => setTab(t)}
              className="px-3 py-2 text-xs font-semibold transition-colors"
              style={{ background: 'none', border: 'none', cursor: 'pointer', borderBottom: tab === t ? `2px solid ${sev.color}` : '2px solid transparent',
                color: tab === t ? sev.color : 'var(--text-3)' }}>
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto p-5">

        {/* ── Overview ── */}
        {tab === 'Overview' && (
          <div className="flex flex-col gap-4">
            {/* Summary */}
            <div className="rounded-xl p-4" style={{ background: `${sev.color}07`, border: `1px solid ${sev.color}20` }}>
              <div className="text-xs font-semibold mb-1.5" style={{ color: sev.color }}>Incident Summary</div>
              <p className="text-xs leading-relaxed" style={{ color: 'var(--text-2)' }}>{inc.summary}</p>
            </div>
            {/* KPIs */}
            <div className="grid grid-cols-4 gap-3">
              {[
                { label: 'MTTD',         value: `${inc.mttd}m`,           color: '#60a5fa', sub: 'time to detect' },
                { label: 'MTTR',         value: inc.mttr ? `${inc.mttr}m` : '—', color: inc.mttr ? '#34d399' : 'var(--text-3)', sub: 'time to resolve' },
                { label: 'Services Hit', value: inc.services.length,      color: sev.color, sub: 'blast radius'   },
                { label: 'RCA Confidence', value: `${inc.rcaConfidence}%`, color: sev.color, sub: 'top hypothesis' },
              ].map(k => (
                <div key={k.label} className="rounded-xl p-3 text-center" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)' }}>
                  <div className="mono font-black text-xl mb-0.5" style={{ color: k.color }}>{k.value}</div>
                  <div className="font-semibold text-xs" style={{ color: 'var(--text-2)' }}>{k.label}</div>
                  <div style={{ fontSize: 9, color: 'var(--text-3)' }}>{k.sub}</div>
                </div>
              ))}
            </div>
            {/* Top hypothesis preview */}
            {inc.hypotheses[0] && (
              <div className="rounded-xl p-4" style={{ background: 'var(--bg-tertiary)', border: `1px solid ${inc.hypotheses[0].color}25` }}>
                <div className="flex items-center gap-2 mb-2">
                  <Brain size={13} style={{ color: '#c084fc' }}/>
                  <span className="text-xs font-semibold" style={{ color: '#c084fc' }}>Top Root Cause Hypothesis</span>
                  <ConfidenceRing pct={inc.hypotheses[0].confidence} color={inc.hypotheses[0].color} size={36}/>
                </div>
                <p className="text-sm font-semibold mb-1">{inc.hypotheses[0].title}</p>
                <p className="text-xs" style={{ color: 'var(--text-3)' }}>{inc.hypotheses[0].evidence[0]?.text}</p>
              </div>
            )}
            {/* Affected services preview */}
            <div>
              <div className="text-xs font-semibold mb-2" style={{ color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Affected Services</div>
              <div className="flex flex-wrap gap-2">
                {inc.services.map(s => (
                  <span key={s} className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs"
                    style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-2)' }}>
                    <Server size={10} style={{ color: sev.color }}/> {s}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Timeline ── */}
        {tab === 'Timeline' && (
          <div className="flex flex-col gap-0">
            <div className="flex items-center gap-2 mb-4 text-xs" style={{ color: 'var(--text-3)' }}>
              <Clock size={11}/> {inc.timeline.length} events · {diffMins(inc.timeline[inc.timeline.length-1].t, inc.timeline[0].t)}m window
              <div className="ml-auto flex gap-2">
                {Object.entries(EVIDENCE_TYPE).map(([k,v]) => (
                  <span key={k} className="flex items-center gap-1" style={{ color: v.color }}>
                    <span style={{ width:6, height:6, borderRadius:'50%', background:v.color, display:'inline-block'}}/>
                    {v.label}
                  </span>
                ))}
              </div>
            </div>
            {inc.timeline.map((e, i) => (
              <TimelineRow key={i} e={e} first={i===0} last={i===inc.timeline.length-1}/>
            ))}
          </div>
        )}

        {/* ── AI Analysis ── */}
        {tab === 'AI Analysis' && (
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2 p-3 rounded-xl text-xs"
              style={{ background: 'rgba(192,132,252,.08)', border: '1px solid rgba(192,132,252,.2)', color: '#c084fc' }}>
              <Brain size={13}/> KRE Observability AI analyzed {inc.timeline.length} events, {inc.correlations.logs.length + inc.correlations.metrics.length} signals, and change history to generate these hypotheses.
            </div>
            {inc.hypotheses.map((h, idx) => (
              <div key={h.id} className="rounded-xl overflow-hidden" style={{ border: `1px solid ${h.color}25` }}>
                <div className="flex items-center gap-3 px-4 py-3" style={{ background: `${h.color}08`, borderBottom: `1px solid ${h.color}15` }}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-3)' }}>#{idx+1}</span>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 4, background: `${h.color}18`, color: h.color }}>{h.category}</span>
                    </div>
                    <p className="font-bold text-sm">{h.title}</p>
                  </div>
                  <ConfidenceRing pct={h.confidence} color={h.color} size={52}/>
                </div>
                <div className="px-4 py-3 flex flex-col gap-2">
                  <div className="text-xs font-semibold mb-1" style={{ color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Supporting Evidence</div>
                  {h.evidence.map((ev, ei) => (
                    <div key={ei} className="flex items-start gap-2 text-xs">
                      <EvidenceChip type={ev.type}/>
                      <span className="flex-1 leading-relaxed" style={{ color: 'var(--text-2)' }}>{ev.text}</span>
                    </div>
                  ))}
                  <div className="mt-2 pt-2 rounded-lg p-3 text-xs leading-relaxed"
                    style={{ background: 'rgba(34,197,94,.06)', border: '1px solid rgba(34,197,94,.2)', color: '#4ade80', borderTop: '1px solid var(--border)' }}>
                    <span style={{ color: '#34d399', fontWeight: 700 }}>✓ Recommended fix: </span>{h.remediation}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Blast Radius ── */}
        {tab === 'Blast Radius' && (
          <BlastRadiusTab inc={inc}/>
        )}

        {/* ── Correlations ── */}
        {tab === 'Correlations' && (
          <div className="flex flex-col gap-5">
            {/* Metrics */}
            <div>
              <div className="text-xs font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                <BarChart2 size={11}/> Correlated Metrics
              </div>
              <div className="grid grid-cols-2 gap-3">
                {inc.correlations.metrics.map((m, i) => (
                  <div key={i} className="rounded-lg px-3 py-2.5" style={{ background: 'var(--bg-tertiary)', border: `1px solid ${m.color}20` }}>
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-xs font-semibold" style={{ color: 'var(--text-2)' }}>{m.name}</span>
                      <span className="flex items-center gap-1 text-xs" style={{ color: m.color }}>
                        {m.trend === 'up' ? <TrendingUp size={10}/> : <TrendingDown size={10}/>}
                        {m.delta}
                      </span>
                    </div>
                    <div className="mono font-bold text-lg" style={{ color: m.color }}>{m.value}</div>
                  </div>
                ))}
              </div>
            </div>
            {/* Logs */}
            <div>
              <div className="text-xs font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                <FileText size={11}/> Correlated Log Lines
              </div>
              <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border)', background: 'var(--bg-primary)', fontFamily: 'Space Mono, monospace' }}>
                {inc.correlations.logs.map((l, i) => (
                  <div key={i} className="px-4 py-2.5" style={{ borderBottom: i < inc.correlations.logs.length - 1 ? '1px solid rgba(255,255,255,.04)' : 'none' }}>
                    <div className="flex items-center gap-3 mb-0.5">
                      <span style={{ fontSize: 9, color: 'var(--text-3)' }}>{fmt(l.time)}</span>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 3,
                        background: l.level==='ERROR'||l.level==='FATAL' ? 'rgba(239,68,68,.15)' : l.level==='WARN' ? 'rgba(245,158,11,.15)' : 'rgba(96,165,250,.15)',
                        color: l.level==='ERROR'||l.level==='FATAL' ? '#f87171' : l.level==='WARN' ? '#fbbf24' : '#60a5fa' }}>{l.level}</span>
                      <span style={{ fontSize: 9, color: '#60a5fa', fontWeight: 600 }}>{l.src}</span>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-2)', wordBreak: 'break-all' }}>{l.msg}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Playbook ── */}
        {tab === 'Playbook' && (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 text-xs p-3 rounded-xl"
              style={{ background: 'rgba(96,165,250,.06)', border: '1px solid rgba(96,165,250,.18)', color: '#93c5fd' }}>
              <ListChecks size={12}/> Automated remediation playbook — {inc.playbook.filter(s=>s.status==='done').length}/{inc.playbook.length} steps complete
            </div>
            {inc.playbook.map(s => {
              const color = s.status==='done' ? '#22c55e' : s.status==='in-progress' ? '#f97316' : '#64748b';
              const icon  = s.status==='done' ? <CheckCircle size={14} style={{ color }}/> :
                            s.status==='in-progress' ? <Play size={13} style={{ color }}/> :
                            <div style={{ width:14, height:14, borderRadius:'50%', border:`2px solid ${color}` }}/>;
              return (
                <div key={s.step} className="flex items-start gap-3 px-4 py-3 rounded-xl"
                  style={{ background: `${color}06`, border: `1px solid ${color}18` }}>
                  {icon}
                  <div className="flex-1">
                    <p className="text-xs leading-relaxed" style={{ color: s.status==='done' ? 'var(--text-3)' : 'var(--text)', textDecoration: s.status==='done' ? 'line-through' : 'none' }}>{s.action}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span style={{ fontSize: 9, color, fontWeight: 700, padding: '1px 5px', borderRadius: 3, background: `${color}12` }}>{s.status.toUpperCase()}</span>
                      <span style={{ fontSize: 9, color: 'var(--text-3)' }}>owner: {s.owner}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

      </div>
    </div>
  );
}

// ─── Service Detail Panel ─────────────────────────────────────────────────────
function ServiceDetailPanel({ service, inc, onClose }) {
  const impCfg = { CRITICAL:'#ef4444', HIGH:'#f97316', MEDIUM:'#f59e0b', LOW:'#94a3b8' };
  const ic = impCfg[service.impact] || '#94a3b8';
  const relLogs = inc.correlations.logs.filter(l => l.src === service.service);
  const relMetrics = inc.correlations.metrics.slice(0, 3);
  return (
    <div className="fixed inset-0 z-50 flex" style={{ background:'rgba(0,0,0,.55)', backdropFilter:'blur(3px)' }} onClick={onClose}>
      <div className="ml-auto h-full w-full overflow-y-auto flex flex-col"
        style={{ maxWidth:480, background:'var(--bg-secondary)', borderLeft:'1px solid var(--border)', boxShadow:'-20px 0 60px rgba(0,0,0,.5)' }}
        onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 shrink-0" style={{ borderBottom:'1px solid var(--border)', background:`${ic}08` }}>
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background:`${ic}15`, border:`1px solid ${ic}30` }}>
            <Server size={16} style={{ color:ic }}/>
          </div>
          <div className="flex-1">
            <div className="font-bold font-mono text-sm">{service.service}</div>
            <div className="text-xs mt-0.5" style={{ color:'var(--text-3)' }}>{service.type} · {inc.pipeline}</div>
          </div>
          <span style={{ fontSize:10, fontWeight:700, padding:'2px 10px', borderRadius:20, background:`${ic}15`, color:ic, border:`1px solid ${ic}30` }}>{service.impact}</span>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--text-3)', cursor:'pointer', fontSize:18, lineHeight:1 }}>✕</button>
        </div>
        <div className="p-5 flex flex-col gap-5 flex-1 overflow-y-auto">
          {/* Impact banner */}
          <div className="rounded-xl p-3 flex items-center gap-3" style={{ background:`${ic}08`, border:`1px solid ${ic}20` }}>
            <AlertTriangle size={14} style={{ color:ic, flexShrink:0 }}/>
            <div>
              <div className="text-xs font-bold" style={{ color:ic }}>Impact: {service.impact}</div>
              <div className="text-xs mt-0.5" style={{ color:'var(--text-2)' }}>{service.affected}</div>
            </div>
          </div>
          {/* Key metrics */}
          <div>
            <div className="text-xs font-semibold mb-2" style={{ color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.7px' }}>Correlated Metrics</div>
            <div className="flex flex-col gap-2">
              {relMetrics.map((m, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-2.5 rounded-lg text-xs"
                  style={{ background:'var(--bg-tertiary)', border:`1px solid ${m.color}18` }}>
                  <span className="mono" style={{ color:'var(--text-2)' }}>{m.name}</span>
                  <div className="flex items-center gap-2">
                    <span className="mono font-bold" style={{ color:m.color }}>{m.value}</span>
                    <span style={{ fontSize:9, color:m.color }}>{m.delta}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* Related log lines */}
          {relLogs.length > 0 && (
            <div>
              <div className="text-xs font-semibold mb-2" style={{ color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.7px' }}>Log Lines ({relLogs.length})</div>
              <div className="rounded-xl overflow-hidden" style={{ border:'1px solid var(--border)', background:'var(--bg-primary)', fontFamily:'Space Mono, monospace' }}>
                {relLogs.map((l, i) => (
                  <div key={i} className="px-3 py-2.5" style={{ borderBottom:i<relLogs.length-1?'1px solid rgba(255,255,255,.04)':'none' }}>
                    <div className="flex items-center gap-2 mb-0.5">
                      <span style={{ fontSize:9, color:'var(--text-3)' }}>{fmt(l.time)}</span>
                      <span style={{ fontSize:9, fontWeight:700, padding:'0 5px', borderRadius:3,
                        background:l.level==='ERROR'?'rgba(239,68,68,.15)':'rgba(96,165,250,.15)',
                        color:l.level==='ERROR'?'#f87171':'#60a5fa' }}>{l.level}</span>
                    </div>
                    <div style={{ fontSize:10, color:'var(--text-2)', wordBreak:'break-all' }}>{l.msg}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {relLogs.length === 0 && (
            <div>
              <div className="text-xs font-semibold mb-2" style={{ color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.7px' }}>Log Lines</div>
              <div className="text-xs py-3 px-4 rounded-lg text-center" style={{ color:'var(--text-3)', background:'var(--bg-tertiary)', border:'1px solid var(--border)' }}>
                No log lines directly attributed to this service in the correlation window
              </div>
            </div>
          )}
          {/* Timeline events for this service */}
          <div>
            <div className="text-xs font-semibold mb-2" style={{ color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.7px' }}>Timeline Events</div>
            {inc.timeline.filter(e => e.src === service.service || e.src.includes(service.service.split('-')[0])).slice(0,5).map((e, i) => {
              const tCfg = EVIDENCE_TYPE[e.type] || { color:'#94a3b8' };
              return (
                <div key={i} className="flex items-start gap-2 mb-2 text-xs">
                  <div style={{ width:7, height:7, borderRadius:'50%', background:tCfg.color, flexShrink:0, marginTop:3 }}/>
                  <div>
                    <span className="mono" style={{ color:'var(--text-3)', marginRight:6 }}>{fmt(e.t)}</span>
                    <EvidenceChip type={e.type}/>
                    <div className="mt-0.5" style={{ color:'var(--text-2)' }}>{e.msg}</div>
                  </div>
                </div>
              );
            })}
            {inc.timeline.filter(e => e.src === service.service || e.src.includes(service.service.split('-')[0])).length === 0 && (
              <div className="text-xs py-2 px-3 rounded" style={{ color:'var(--text-3)', background:'var(--bg-tertiary)' }}>No direct timeline entries for this service</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Incident list item ───────────────────────────────────────────────────────
function IncidentRow({ inc, selected, onClick }) {
  const sev     = SEV[inc.severity] || SEV.MEDIUM;
  const doneSteps = inc.playbook.filter(s => s.status === 'done').length;
  const pct     = Math.round((doneSteps / inc.playbook.length) * 100);
  return (
    <button onClick={onClick}
      className="w-full text-left px-4 py-3 flex flex-col gap-1.5 transition-all"
      style={{ background: selected ? `${sev.color}10` : 'transparent', borderLeft: `3px solid ${selected ? sev.color : 'transparent'}`, borderBottom: '1px solid var(--border)', cursor: 'pointer' }}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="mono text-xs font-bold" style={{ color: sev.color }}>{inc.id}</span>
        <StatusBadge status={inc.status}/>
        <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: sev.bg, color: sev.color }}>{inc.severity}</span>
      </div>
      <p className="text-xs font-semibold leading-snug" style={{ color: 'var(--text)' }}>{inc.title}</p>
      <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-3)' }}>
        <span className="mono">{fmt(inc.detectedAt)}</span>
        <span>·</span>
        <span>MTTD {inc.mttd}m</span>
        {inc.mttr && <><span>·</span><span>MTTR {inc.mttr}m</span></>}
      </div>
      {/* Playbook progress */}
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1 rounded-full overflow-hidden" style={{ background: 'var(--border)' }}>
          <div style={{ width:`${pct}%`, height:'100%', background: pct===100 ? '#22c55e' : sev.color, borderRadius: 99 }}/>
        </div>
        <span style={{ fontSize: 9, color: 'var(--text-3)', width: 28, flexShrink: 0 }}>{pct}%</span>
      </div>
    </button>
  );
}

// ─── Main view ────────────────────────────────────────────────────────────────
export default function RootCauseView() {
  const [selected,    setSelected]    = useState(INCIDENTS[0].id);
  const [search,      setSearch]      = useState('');
  const [filterSev,   setFilterSev]   = useState('ALL');
  const [filterStat,  setFilterStat]  = useState('ALL');

  const selInc = INCIDENTS.find(i => i.id === selected) || INCIDENTS[0];

  const filtered = INCIDENTS.filter(i => {
    if (filterSev !== 'ALL'  && i.severity !== filterSev)                     return false;
    if (filterStat !== 'ALL' && i.status   !== filterStat)                    return false;
    if (search && !i.title.toLowerCase().includes(search.toLowerCase()) &&
                  !i.id.toLowerCase().includes(search.toLowerCase()))          return false;
    return true;
  });

  const openCount = INCIDENTS.filter(i => i.status !== 'RESOLVED').length;

  return (
    <div className="flex flex-col h-full">
      {/* ── Header ── */}
      <div className="px-5 py-4 shrink-0 flex items-center gap-4 flex-wrap"
        style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
        <div className="w-9 h-9 rounded-xl flex items-center justify-center"
          style={{ background: 'linear-gradient(135deg,#ef4444,#f97316)', boxShadow: '0 4px 14px rgba(239,68,68,.4)' }}>
          <Microscope size={16} className="text-white"/>
        </div>
        <div>
          <h1 className="text-base font-bold">Root Cause Investigation</h1>
          <p className="text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>
            AI-powered pipeline failure analysis · timeline correlation · blast radius · automated playbooks
          </p>
        </div>
        {/* KPI chips */}
        <div className="flex items-center gap-2 ml-2 flex-wrap">
          {[
            { label: 'Open',     value: openCount,   color: '#ef4444' },
            { label: 'Avg MTTD', value: `${avgMttd}m`, color: '#60a5fa' },
            { label: 'Avg MTTR', value: `${avgMttr}m`, color: '#34d399' },
          ].map(k => (
            <div key={k.label} className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs"
              style={{ background: `${k.color}12`, border: `1px solid ${k.color}25` }}>
              <span style={{ color: 'var(--text-3)' }}>{k.label}</span>
              <span className="mono font-bold" style={{ color: k.color }}>{k.value}</span>
            </div>
          ))}
        </div>
        {/* MTTD comparison pill */}
        <div className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold"
          style={{ background: 'rgba(34,197,94,.1)', border: '1px solid rgba(34,197,94,.25)', color: '#4ade80' }}>
          <TrendingDown size={12}/> MTTD improved 78% vs manual investigation
        </div>
      </div>

      {/* ── Body: list + panel ── */}
      <div className="flex flex-1 overflow-hidden">

        {/* Incident list */}
        <div className="flex flex-col shrink-0" style={{ width: 320, borderRight: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
          {/* Filters */}
          <div className="p-3 flex flex-col gap-2 shrink-0" style={{ borderBottom: '1px solid var(--border)' }}>
            <div className="relative">
              <Search size={12} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }}/>
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search incidents…"
                style={{ width: '100%', padding: '7px 10px 7px 30px', borderRadius: 8, fontSize: 12,
                  background: 'var(--bg-primary)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }}/>
            </div>
            <div className="flex gap-2">
              <select value={filterSev} onChange={e => setFilterSev(e.target.value)}
                style={{ flex: 1, padding: '5px 8px', borderRadius: 8, fontSize: 11, background: 'var(--bg-primary)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }}>
                <option value="ALL">All Severity</option>
                <option value="CRITICAL">Critical</option>
                <option value="HIGH">High</option>
                <option value="MEDIUM">Medium</option>
              </select>
              <select value={filterStat} onChange={e => setFilterStat(e.target.value)}
                style={{ flex: 1, padding: '5px 8px', borderRadius: 8, fontSize: 11, background: 'var(--bg-primary)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }}>
                <option value="ALL">All Status</option>
                <option value="INVESTIGATING">Investigating</option>
                <option value="CONFIRMED">RCA Confirmed</option>
                <option value="MITIGATED">Mitigated</option>
                <option value="RESOLVED">Resolved</option>
              </select>
            </div>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="p-8 text-center text-xs" style={{ color: 'var(--text-3)' }}>No incidents match filter</div>
            ) : (
              filtered.map(inc => (
                <IncidentRow key={inc.id} inc={inc}
                  selected={selected === inc.id}
                  onClick={() => setSelected(inc.id)}/>
              ))
            )}
          </div>

          {/* MTTD/MTTR comparison */}
          <div className="p-3 shrink-0" style={{ borderTop: '1px solid var(--border)' }}>
            <div className="text-xs font-semibold mb-2" style={{ color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Investigation Velocity</div>
            <div className="flex flex-col gap-1.5">
              {[
                { label: 'Avg MTTD — with KRE', value: `${avgMttd}m`, color: '#34d399', was: '38m' },
                { label: 'Avg MTTR — with KRE',  value: `${avgMttr}m`, color: '#60a5fa', was: '430m' },
              ].map(m => (
                <div key={m.label} className="flex items-center gap-2 text-xs">
                  <div style={{ flex: 1 }}>
                    <div style={{ color: 'var(--text-3)', fontSize: 9, marginBottom: 2 }}>{m.label}</div>
                    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--bg-primary)' }}>
                      <div style={{ width: '30%', height: '100%', background: m.color, borderRadius: 99 }}/>
                    </div>
                  </div>
                  <span className="mono font-bold shrink-0" style={{ color: m.color, fontSize: 11 }}>{m.value}</span>
                  <span style={{ color: 'var(--text-3)', fontSize: 9, flexShrink: 0 }}>was {m.was}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Investigation detail */}
        <div className="flex-1 overflow-hidden">
          {selInc && <InvestigationPanel inc={selInc}/>}
        </div>
      </div>
    </div>
  );
}
